import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Button,
  useTheme,
  CircularProgress,
  Alert,
  InputAdornment,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Paper,
  Divider,
  Chip,
  Avatar,
  Badge,
  useMediaQuery,
  Skeleton,
} from "@mui/material";
import {
  PictureAsPdf,
  Print as PrintIcon,
  ArrowDropUp,
  ArrowDropDown,
  AccessTime,
  Paid,
  Close,
  History,
  ShowChart,
  MoneyOff,
  TrendingUp,
  Summarize,
  Receipt,
  DateRange,
  FilterList,
  CalendarToday,
  LocalPharmacy,
  MedicalServices,
  Science,
  Build,
  AccountBalanceWallet,
  Undo,
  Favorite,
  Healing,
  PointOfSale,
  Payments,
  AccountTree,
  SettingsBackupRestore,
  Event,
  Today,
  AllInclusive,
  Person,
  ArrowForward,
} from "@mui/icons-material";
import { Bar, Pie, Doughnut, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title as ChartTitle,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from "chart.js";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { useNavigate } from "react-router-dom";
import {
  format,
  subDays,
  startOfWeek,
  startOfMonth,
  startOfYear,
  isAfter,
  isValid,
  subMonths,
  subYears,
  eachDayOfInterval,
  eachWeekOfInterval,
  eachMonthOfInterval,
} from "date-fns";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useAuth } from "../../../../context";
// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ChartTitle,
  ChartTooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);
const DateTimeInput = React.forwardRef(({ value, onClick, label }, ref) => (
  <TextField
    fullWidth
    size="small"
    label={label}
    value={value}
    onClick={onClick}
    ref={ref}
    InputProps={{
      endAdornment: (
        <InputAdornment position="end">
          <AccessTime fontSize="small" color="action" />
        </InputAdornment>
      ),
    }}
    sx={{
      "& .MuiOutlinedInput-root": {
        borderRadius: "6px",
        "& fieldset": {
          borderColor: "divider",
        },
        "&:hover fieldset": {
          borderColor: "primary.main",
        },
        "&.Mui-focused fieldset": {
          borderColor: "primary.main",
          borderWidth: "1px",
        },
      },
    }}
  />
));
const formatCurrency = (value, currency = "AFN") => {
  if (typeof value !== "number" || isNaN(value)) return "؋0";
  switch (currency) {
    case "USD":
      return `$ ${value.toLocaleString()}`;
    case "EUR":
      return `€ ${value.toLocaleString()}`;
    default:
      return `؋ ${value.toLocaleString()}`;
  }
};
const HistoryDialog = ({ open, onClose, history, title }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const formatEmployeeInfo = (employee) => {
    if (!employee) return "-";
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <Person fontSize="small" />
        <Box>
          <Typography variant="body2" fontWeight="bold">
            {employee.name || "N/A"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ID: {employee.id || "N/A"}
          </Typography>
        </Box>
      </Box>
    );
  };
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      if (!isValid(date)) return "-";
      return <Typography variant="body2">{format(date, "PPpp")}</Typography>;
    } catch (error) {
      return "-";
    }
  };
  const formatTime = (timeString) => {
    if (!timeString) return "-";
    try {
      const time = new Date(timeString);
      if (!isValid(time)) return "-";
      return <Typography variant="body2">{format(time, "p")}</Typography>;
    } catch (error) {
      return "-";
    }
  };
  const getColumns = () => {
    if (!history || history.length === 0) return [];
    const sampleItem = history[0];
    const columns = [];
    // Common columns for all types
    columns.push("id");
    columns.push("date");
    if (sampleItem.patient) columns.push("patient");
    if (sampleItem.employee) columns.push("employee");
    if (sampleItem.doctor) columns.push("doctor");
    if (sampleItem.co_doctor) columns.push("co_doctor");
    if (sampleItem.anesthetist) columns.push("anesthetist");
    if (sampleItem.assigned_doctor) columns.push("assigned_doctor");
    // Amount fields with currency support
    if (sampleItem.amount !== undefined) columns.push("amount");
    if (sampleItem.fees !== undefined) columns.push("fees");
    if (sampleItem.due !== undefined) columns.push("due");
    if (sampleItem.total_price !== undefined) columns.push("total_price");
    if (sampleItem.total_amount !== undefined) columns.push("total_amount");
    if (sampleItem.total_surgery_amount !== undefined)
      columns.push("total_surgery_amount");
    if (sampleItem.discount !== undefined) columns.push("discount");
    // Status fields
    if (sampleItem.status !== undefined) columns.push("status");
    if (sampleItem.payment_status !== undefined) columns.push("payment_status");
    if (sampleItem.is_paid !== undefined) columns.push("is_paid");
    if (sampleItem.is_urgent !== undefined) columns.push("is_urgent");
    // Type fields
    if (sampleItem.ultrasound_type !== undefined)
      columns.push("ultrasound_type");
    if (sampleItem.xray_type !== undefined) columns.push("xray_type");
    if (sampleItem.echo_type !== undefined) columns.push("echo_type");
    if (sampleItem.expense_type !== undefined) columns.push("expense_type");
    if (sampleItem.expense_source !== undefined) columns.push("expense_source");
    if (sampleItem.surgery_type !== undefined) columns.push("surgery_type");
    if (sampleItem.surgery_state !== undefined) columns.push("surgery_state");
    if (sampleItem.procedure_type !== undefined) columns.push("procedure_type");
    if (sampleItem.currency !== undefined) columns.push("currency");
    // Other fields
    if (sampleItem.description !== undefined) columns.push("description");
    if (sampleItem.visit_date !== undefined) columns.push("visit_date");
    if (sampleItem.visit_time !== undefined) columns.push("visit_time");
    if (sampleItem.procedure_date !== undefined) columns.push("procedure_date");
    if (sampleItem.surgery_date !== undefined) columns.push("surgery_date");
    if (sampleItem.company_name !== undefined) columns.push("company_name");
    if (sampleItem.invoice_number !== undefined) columns.push("invoice_number");
    if (sampleItem.month !== undefined) columns.push("month");
    if (sampleItem.year !== undefined) columns.push("year");
    if (sampleItem.urgency !== undefined) columns.push("urgency");
    // Medicine order specific
    if (sampleItem.items !== undefined) columns.push("items");
    // Prescription specific
    if (sampleItem.drugs !== undefined) columns.push("drugs");
    return columns;
  };
  const columns = getColumns();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      aria-labelledby="history-dialog-title"
    >
      <DialogTitle
        id="history-dialog-title"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          fontWeight: "bold",
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <History aria-hidden="true" />
          <span>{title.toUpperCase()} HISTORY</span>
        </Box>
        <IconButton onClick={onClose} color="inherit" aria-label="close">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {history?.length > 0 ? (
          <TableContainer sx={{ maxHeight: "calc(100vh - 200px)" }}>
            <Table stickyHeader aria-label="History details table" size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: theme.palette.grey[100] }}>
                  {columns.map((col) => (
                    <TableCell key={col}>
                      {col.replace(/_/g, " ").toUpperCase()}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((item, index) => (
                  <TableRow key={index} hover>
                    {columns.map((col) => {
                      let value = item[col];
                      // Special handling for ID column
                      if (col === "id") {
                        return (
                          <TableCell key={col}>
                            <Typography variant="body2" fontWeight="bold">
                              #{value}
                            </Typography>
                          </TableCell>
                        );
                      }
                      // Date formatting with error handling
                      if (col === "date" || col.endsWith("_date")) {
                        return (
                          <TableCell key={col}>{formatDate(value)}</TableCell>
                        );
                      }
                      // Time formatting with error handling
                      if (col === "visit_time" || col.endsWith("_time")) {
                        return (
                          <TableCell key={col}>{formatTime(value)}</TableCell>
                        );
                      }
                      // Person info formatting
                      if (
                        col === "patient" ||
                        col === "employee" ||
                        col === "doctor" ||
                        col === "co_doctor" ||
                        col === "anesthetist" ||
                        col === "assigned_doctor"
                      ) {
                        return (
                          <TableCell key={col}>
                            {formatEmployeeInfo(value)}
                          </TableCell>
                        );
                      }
                      // Currency amount formatting
                      if (
                        col === "amount" ||
                        col === "fees" ||
                        col === "due" ||
                        col === "total_price" ||
                        col === "total_amount" ||
                        col === "total_surgery_amount" ||
                        col === "discount"
                      ) {
                        // Get currency from the item if available
                        const currency = item.currency || "AFN";
                        return (
                          <TableCell key={col}>
                            {formatCurrency(value || 0, currency)}
                          </TableCell>
                        );
                      }
                      // Boolean formatting
                      if (col === "is_paid" || col === "is_urgent") {
                        return (
                          <TableCell key={col}>
                            <Chip
                              label={value ? "Yes" : "No"}
                              size="small"
                              color={value ? "success" : "error"}
                            />
                          </TableCell>
                        );
                      }
                      // Nested objects (items, drugs)
                      if (col === "items" || col === "drugs") {
                        if (
                          !value ||
                          !Array.isArray(value) ||
                          value.length === 0
                        ) {
                          return <TableCell key={col}>-</TableCell>;
                        }
                        return (
                          <TableCell key={col}>
                            <Box sx={{ maxHeight: 100, overflowY: "auto" }}>
                              {value.map((subItem, subIndex) => (
                                <Box
                                  key={subIndex}
                                  mb={1}
                                  p={1}
                                  bgcolor="grey.100"
                                  borderRadius={1}
                                >
                                  <Typography variant="body2" fontWeight="bold">
                                    {subItem.medicine_name ||
                                      subItem.stock_medicine_name ||
                                      `Item ${subIndex + 1}`}
                                  </Typography>
                                  <Typography variant="caption" display="block">
                                    Qty: {subItem.quantity || 0}
                                  </Typography>
                                  {subItem.dosage && (
                                    <Typography
                                      variant="caption"
                                      display="block"
                                    >
                                      Dosage: {subItem.dosage}
                                    </Typography>
                                  )}
                                  {subItem.price !== undefined && (
                                    <Typography
                                      variant="caption"
                                      display="block"
                                    >
                                      Price:{" "}
                                      {formatCurrency(
                                        subItem.price,
                                        item.currency || "AFN"
                                      )}
                                    </Typography>
                                  )}
                                  {subItem.total_price !== undefined && (
                                    <Typography
                                      variant="caption"
                                      display="block"
                                    >
                                      Total:{" "}
                                      {formatCurrency(
                                        subItem.total_price,
                                        item.currency || "AFN"
                                      )}
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          </TableCell>
                        );
                      }
                      // Status formatting with chips
                      if (col === "status" || col === "payment_status") {
                        let chipColor = "default";
                        if (typeof value === "string") {
                          const lowerValue = value.toLowerCase();
                          if (
                            lowerValue.includes("complete") ||
                            lowerValue.includes("paid") ||
                            lowerValue.includes("success")
                          ) {
                            chipColor = "success";
                          } else if (
                            lowerValue.includes("pending") ||
                            lowerValue.includes("wait")
                          ) {
                            chipColor = "warning";
                          } else if (
                            lowerValue.includes("cancel") ||
                            lowerValue.includes("fail")
                          ) {
                            chipColor = "error";
                          }
                        }
                        return (
                          <TableCell key={col}>
                            <Chip
                              label={value || "Unknown"}
                              size="small"
                              color={chipColor}
                            />
                          </TableCell>
                        );
                      }
                      // Handle other object types
                      if (typeof value === "object" && value !== null) {
                        return (
                          <TableCell key={col}>
                            {JSON.stringify(value)}
                          </TableCell>
                        );
                      }
                      // Default text display
                      return (
                        <TableCell key={col}>
                          {value?.toString() || "-"}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            p={4}
            textAlign="center"
          >
            <History color="disabled" sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No History Available
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={1}>
              There are no records to display for this section.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" startIcon={<Close />}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
const StatCard = ({
  icon,
  title,
  value,
  amount,
  onClick,
  theme,
  history,
  loading = false,
  highlight,
  cardSx,
}) => {
  if (loading) {
    return (
      <Card sx={{ height: "100%", p: 2 }}>
        <Skeleton variant="rectangular" width="100%" height={180} />
      </Card>
    );
  }
  // Capitalize each word in the title
  const capitalizeTitle = (str) =>
    str.replace(/\b\w/g, (char) => char.toUpperCase());
  return (
    <Card
      onClick={onClick}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        cursor: history?.length > 0 ? "pointer" : "default",
        transition: "all 0.3s ease",
        borderLeft: `4px solid ${
          highlight === "negative"
            ? theme.palette.error.main
            : highlight === "positive"
            ? theme.palette.success.main
            : theme.palette.primary.main
        }`,
        "&:hover": {
          boxShadow: history?.length > 0 ? theme.shadows[6] : undefined,
          transform: history?.length > 0 ? "translateY(-4px)" : undefined,
        },
        ...cardSx,
      }}
      aria-label={`${title} card`}
      role={history?.length > 0 ? "button" : undefined}
      tabIndex={history?.length > 0 ? 0 : -1}
      onKeyDown={(e) => {
        if (history?.length > 0 && (e.key === "Enter" || e.key === " ")) {
          onClick();
        }
      }}
    >
      <CardHeader
        avatar={
          <Avatar
            sx={{
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
            }}
          >
            {icon}
          </Avatar>
        }
        title={
          <Typography variant="subtitle1" fontWeight="bold" noWrap>
            {capitalizeTitle(title)}
          </Typography>
        }
        action={
          <Badge
            badgeContent={history?.length || 0}
            color="primary"
            sx={{ mr: 1 }}
            showZero
          >
            <History sx={{ mr: 1, fontSize: 22 }} />
          </Badge>
        }
      />
      <CardContent sx={{ flexGrow: 1 }}>
        <Box display="flex" flexDirection="column" gap={1}>
          {Array.isArray(amount) ? (
            <Box>
              {amount.map((amt, idx) => (
                <Typography
                  key={idx}
                  variant={idx === 0 ? "h5" : "body1"}
                  fontWeight="bold"
                  sx={{
                    lineHeight: 1.2,
                    mt: idx > 0 ? 1 : 0,
                  }}
                >
                  {amt}
                </Typography>
              ))}
            </Box>
          ) : (
            <Typography
              variant="h5"
              fontWeight="bold"
              sx={{
                lineHeight: 1.2,
              }}
            >
              {amount || formatCurrency(0)}
            </Typography>
          )}
        </Box>
      </CardContent>
      <Box
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: theme.palette.grey[100],
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {history?.length > 0 ? "View Details" : "No Transactions"}
        </Typography>
        <ArrowForward color="action" />
      </Box>
    </Card>
  );
};
const FinanceDashboard = () => {
  const theme = useTheme();
  const auth = useAuth();
  const token = auth?.user?.token;
  const reportRef = useRef();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [timeRange, setTimeRange] = useState("monthly");
  const [fetchParams, setFetchParams] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [historyDialog, setHistoryDialog] = useState({
    open: false,
    history: [],
    title: "",
  });
  const { data, isLoading, isError, error, refetch } = useFetchObjects(
    ["finance-report", fetchParams],
    fetchParams,
    token
  );
  useEffect(() => {
    updateFetchParams();
  }, [timeRange, startDate, endDate]);
  const updateFetchParams = () => {
    let params = "reception-reports/";
    if (timeRange === "all") {
      params += "?type=all";
    } else if (timeRange === "custom") {
      if (startDate && endDate) {
        params += `?type=&start_datetime=${format(
          startDate,
          "yyyy-MM-dd'T'HH:mm:ss"
        )}&end_datetime=${format(endDate, "yyyy-MM-dd'T'HH:mm:ss")}`;
      } else {
        params += "?type=";
      }
    } else {
      params += `?type=${timeRange}`;
    }
    setFetchParams(params);
  };
  // --- AGGREGATE USD/EUR FROM SPECIAL PAYMENTS ---
  const getSpecialPaymentsCurrencyTotals = (special_payments) => {
    const currencyTotals = {};
    if (!special_payments) return currencyTotals;
    Object.values(special_payments).forEach((payment) => {
      if (payment && payment.total && typeof payment.total === "object") {
        Object.entries(payment.total).forEach(([currency, value]) => {
          if (!currencyTotals[currency]) currencyTotals[currency] = 0;
          currencyTotals[currency] += Number(value) || 0;
        });
      }
    });
    return currencyTotals;
  };
  // --- PREPARE SUMMARY AMOUNTS ---
  let summaryRevenue = [];
  let summaryNetIncome = [];
  let summaryExpenses = [];
  let currencyOrder = ["AFN", "USD", "EUR"];
  if (data && data.summary) {
    // Revenue
    if (typeof data.summary.total_revenue === "number") {
      summaryRevenue.push({
        currency: "AFN",
        value: data.summary.total_revenue,
      });
    }
    // Expenses
    if (typeof data.summary.total_expenses === "number") {
      summaryExpenses.push({
        currency: "AFN",
        value: data.summary.total_expenses,
      });
    }
    // Net Income
    if (typeof data.summary.net_income === "number") {
      summaryNetIncome.push({
        currency: "AFN",
        value: data.summary.net_income,
      });
    }
    // Special Payments (USD/EUR)
    const specialTotals = getSpecialPaymentsCurrencyTotals(
      data.special_payments
    );
    currencyOrder.forEach((cur) => {
      if (cur !== "AFN" && specialTotals[cur] > 0) {
        summaryRevenue.push({ currency: cur, value: specialTotals[cur] });
        // Net income: add as positive (if you want to show net income in USD/EUR, you can adjust this logic)
        summaryNetIncome.push({ currency: cur, value: specialTotals[cur] });
      }
    });
  }
  const handleTimeRangeChange = (e) => {
    const range = e.target.value;
    setTimeRange(range);
    const now = new Date();
    switch (range) {
      case "daily":
        setStartDate(new Date(format(now, "yyyy-MM-dd")));
        setEndDate(now);
        break;
      case "weekly":
        setStartDate(startOfWeek(now));
        setEndDate(now);
        break;
      case "monthly":
        setStartDate(startOfMonth(now));
        setEndDate(now);
        break;
      case "yearly":
        setStartDate(startOfYear(now));
        setEndDate(now);
        break;
      case "all":
        setStartDate(null);
        setEndDate(null);
        break;
      default:
        break;
    }
  };
  const handleCustomDateChange = () => {
    if (!startDate || !endDate) return;
    setTimeRange("custom");
  };
  const handleViewHistory = (history, title) => {
    if (!history || history.length === 0) return;
    setHistoryDialog({
      open: true,
      history: Array.isArray(history) ? history : [],
      title,
    });
  };
  const handleChartClick = (label) => {
    // Define navigation routes based on chart labels
    const routes = {
      APPOINTMENTS: "/reception/appointments",
      PRESCRIPTIONS: "/pharmacy/prescriptions",
      "MEDICINE ORDERS": "/pharmacy/orders",
      ULTRASOUNDS: "/radiology/ultrasounds",
      "X-RAYS": "/radiology/xrays",
      ECHOS: "/cardiology/echos",
      "LAB TESTS": "/laboratory/tests",
      "DENTAL PAYMENTS": "/dental/payments",
      SURGERIES: "/surgery/list",
      "MEDICINE RETURNS": "/pharmacy/returns",
      "HT PATIENT PAYMENTS": "/reception/ht-patients",
      "PROCEDURE PAYMENTS": "/reception/procedures",
      "PURCHASE PAYMENTS": "/finance/purchases",
      EXPENSES: "/finance/expenses",
      PAYROLL: "/finance/payroll",
      ADVANCES: "/finance/advances",
      BONUSES: "/finance/bonuses",
    };
    if (routes[label]) navigate(routes[label]);
  };
  const prepareChartData = () => {
    if (!data) return null;
    // Use API summary values for totals
    const totalRevenue = data.summary?.total_revenue || 0;
    const totalExpenses = data.summary?.total_expenses || 0;
    const netIncome = data.summary?.net_income || 0;
    // Income breakdown (do not sum for totalRevenue)
    const incomeSources = [
      {
        name: "Appointments",
        value: data.medical_services?.appointments?.total || 0,
        history: data.medical_services?.appointments?.history || [],
        count: data.medical_services?.appointments?.count || 0,
      },
      {
        name: "Prescriptions",
        value: data.medical_services?.prescriptions?.total || 0,
        history: data.medical_services?.prescriptions?.history || [],
        count: data.medical_services?.prescriptions?.count || 0,
      },
      {
        name: "Medicine Orders",
        value: data.medical_services?.medicine_orders?.total || 0,
        history: data.medical_services?.medicine_orders?.history || [],
        count: data.medical_services?.medicine_orders?.count || 0,
      },
      {
        name: "Ultrasounds",
        value: data.medical_services?.ultrasounds?.total || 0,
        history: data.medical_services?.ultrasounds?.history || [],
        count: data.medical_services?.ultrasounds?.count || 0,
      },
      {
        name: "X-Rays",
        value: data.medical_services?.xrays?.total || 0,
        history: data.medical_services?.xrays?.history || [],
        count: data.medical_services?.xrays?.count || 0,
      },
      {
        name: "Echos",
        value: data.medical_services?.echos?.total || 0,
        history: data.medical_services?.echos?.history || [],
        count: data.medical_services?.echos?.count || 0,
      },
      {
        name: "Lab Tests",
        value: data.medical_services?.laboratory_tests?.total || 0,
        history: data.medical_services?.laboratory_tests?.history || [],
        count: data.medical_services?.laboratory_tests?.count || 0,
      },
      {
        name: "Dental Payments",
        value: data.medical_services?.dental_payments?.total || 0,
        history: data.medical_services?.dental_payments?.history || [],
        count: data.medical_services?.dental_payments?.count || 0,
      },
      {
        name: "Surgeries",
        value: data.medical_services?.surgeries?.total || 0,
        history: data.medical_services?.surgeries?.history || [],
        count: data.medical_services?.surgeries?.count || 0,
      },
      {
        name: "Medicine Returns",
        value: data.medical_services?.medicine_returns?.total || 0,
        history: data.medical_services?.medicine_returns?.history || [],
        count: data.medical_services?.medicine_returns?.count || 0,
      },
      {
        name: "HT Patient Payments",
        value: data.special_payments?.ht_patient_payments?.total_afn || 0,
        history: data.special_payments?.ht_patient_payments?.history || [],
        count: data.special_payments?.ht_patient_payments?.count || 0,
      },
      {
        name: "Procedure Payments",
        value: data.special_payments?.procedure_payments?.total_afn || 0,
        history: data.special_payments?.procedure_payments?.history || [],
        count: data.special_payments?.procedure_payments?.count || 0,
      },
    ].filter((item) => item.value > 0);
    // Expense breakdown (do not sum for totalExpenses)
    const expenses = [
      {
        name: "Purchase Payments",
        value: data.expenses?.purchase_payments?.total || 0,
        history: data.expenses?.purchase_payments?.history || [],
        count: data.expenses?.purchase_payments?.count || 0,
      },
      {
        name: "Expenses",
        value: data.expenses?.expenses?.total || 0,
        history: data.expenses?.expenses?.history || [],
        count: data.expenses?.expenses?.count || 0,
      },
      {
        name: "Payroll",
        value: data.expenses?.payroll?.total || 0,
        history: data.expenses?.payroll?.history || [],
        count: data.expenses?.payroll?.count || 0,
      },
      {
        name: "Advances",
        value: data.expenses?.advances?.total || 0,
        history: data.expenses?.advances?.history || [],
        count: data.expenses?.advances?.count || 0,
      },
      {
        name: "Bonuses",
        value: data.expenses?.bonuses?.total || 0,
        history: data.expenses?.bonuses?.history || [],
        count: data.expenses?.bonuses?.count || 0,
      },
    ].filter((item) => item.value > 0);
    // Collection status (use API summary for collected)
    const collected = totalRevenue;
    const pending = 0; // This would need actual pending amount calculation
    // Top income sources
    const topIncomeSources = [...incomeSources]
      .sort((a, b) => b.value - a.value)
      .slice(0, 3);

    // Generate trend data from backend histories
    const generateTrendData = () => {
      const incomeHistories = [
        data.medical_services?.appointments?.history || [],
        data.medical_services?.prescriptions?.history || [],
        data.medical_services?.medicine_orders?.history || [],
        data.medical_services?.ultrasounds?.history || [],
        data.medical_services?.xrays?.history || [],
        data.medical_services?.echos?.history || [],
        data.medical_services?.laboratory_tests?.history || [],
        data.medical_services?.dental_payments?.history || [],
        data.medical_services?.surgeries?.history || [],
        data.medical_services?.emergency_payments?.history || [],
        data.medical_services?.ipd?.history || [],
        // special payments
        data.special_payments?.ht_patient_payments?.history || [],
        data.special_payments?.procedure_payments?.history || [],
      ];
      const expenseHistories = [
        data.expenses?.purchase_payments?.history || [],
        data.expenses?.expenses?.history || [],
        data.expenses?.payroll?.history || [],
        data.expenses?.advances?.history || [],
        data.expenses?.bonuses?.history || [],
      ];

      const sumByDate = (histories, amountKey = "amount") => {
        const map = new Map();
        histories.forEach((list) => {
          list.forEach((item) => {
            const d = item.date ? String(item.date).slice(0, 10) : "";
            if (!d) return;
            const prev = map.get(d) || 0;
            const amt = Number(item[amountKey] ?? item.value ?? 0) || 0;
            map.set(d, prev + amt);
          });
        });
        return map;
      };

      const incomeMap = sumByDate(incomeHistories);
      const expenseMap = sumByDate(expenseHistories);
      // Union dates
      const allDates = Array.from(
        new Set([...incomeMap.keys(), ...expenseMap.keys()])
      ).sort();

      // Limit number of points based on timeRange for readability
      let labels = allDates;
      if (labels.length === 0) {
        return { labels: [], revenue: [], expenses: [] };
      }
      const maxPoints =
        timeRange === "yearly" ? 60 : timeRange === "all" ? 60 : 30;
      if (labels.length > maxPoints) {
        labels = labels.slice(-maxPoints);
      }

      const revenueData = labels.map((d) => incomeMap.get(d) || 0);
      const expensesData = labels.map((d) => expenseMap.get(d) || 0);

      // Format labels nicely
      const formattedLabels = labels.map((d) => {
        const dt = new Date(d);
        if (timeRange === "yearly") return format(dt, "yyyy");
        if (timeRange === "monthly" || timeRange === "all")
          return format(dt, "MMM yyyy");
        return format(dt, "MMM dd");
      });

      return {
        labels: formattedLabels,
        revenue: revenueData,
        expenses: expensesData,
      };
    };

    const trends = generateTrendData();

    return {
      summary: {
        totalRevenue,
        totalExpenses,
        netIncome,
        collected,
        pending,
      },
      incomeBreakdown: {
        labels: incomeSources.map((item) => item.name.toUpperCase()),
        data: incomeSources.map((item) => item.value),
        history: incomeSources.map((item) => item.history),
        colors: incomeSources.map((_, i) =>
          theme.palette.primary.main
            .replace(")", `, ${0.5 + i * 0.05})`)
            .replace("rgb", "rgba")
        ),
      },
      expenseBreakdown: {
        labels: expenses.map((item) => item.name.toUpperCase()),
        data: expenses.map((item) => item.value),
        history: expenses.map((item) => item.history),
        colors: [
          theme.palette.error.main,
          theme.palette.warning.main,
          theme.palette.info.main,
          theme.palette.success.main,
          theme.palette.text.secondary,
        ].slice(0, expenses.length),
      },
      collectionStatus: {
        labels: ["Collected", "Pending"],
        data: [collected, pending],
      },
      topIncomeSources,
      trends,
    };
  };
  const chartData = prepareChartData();
  const handleDownloadPDF = async () => {
    if (!reportRef.current) return;
    const elementsToHide = document.querySelectorAll(".no-print");
    elementsToHide.forEach((el) => (el.style.display = "none"));
    const canvas = await html2canvas(reportRef.current, {
      scale: 2,
      useCORS: true,
      scrollY: -window.scrollY,
      windowWidth: document.documentElement.offsetWidth,
      windowHeight: document.documentElement.offsetHeight,
    });
    elementsToHide.forEach((el) => (el.style.display = ""));
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("landscape");
    const imgProps = pdf.getImageProperties(imgData);
    const pdfWidth = pdf.internal.pageSize.getWidth() - 20;
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
    pdf.addImage(imgData, "PNG", 10, 10, pdfWidth, pdfHeight);
    pdf.save("financial-report.pdf");
  };
  const handlePrint = () => {
    const originalContents = document.body.innerHTML;
    const printContents = reportRef.current.innerHTML;
    const printStyles = `
      <style>
        @page { size: landscape; margin: 10mm; }
        body { font-family: Arial, sans-serif; }
        .no-print { display: none !important; }
        .MuiCard-root { box-shadow: none !important; page-break-inside: avoid; }
        .MuiGrid-container { display: flex !important; flex-wrap: wrap !important; }
        canvas { max-width: 100% !important; height: auto !important; }
      </style>
    `;
    document.body.innerHTML = printStyles + printContents;
    window.print();
    document.body.innerHTML = originalContents;
    window.location.reload();
  };
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }
  if (isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {error?.message || "Failed to load financial data"}
        </Alert>
        <Button onClick={refetch} variant="contained" sx={{ mt: 2 }}>
          Retry
        </Button>
      </Box>
    );
  }
  if (!data || !chartData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No data available. Please apply filters.</Alert>
      </Box>
    );
  }
  return (
    <Box sx={{ p: 3 }} ref={reportRef}>
      {/* Header and Filters */}
      <Card elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="h5" fontWeight="bold">
              Financial Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {data.date_range?.start && data.date_range?.end ? (
                <>
                  {format(new Date(data.date_range.start), "MMM d, yyyy")} -{" "}
                  {format(new Date(data.date_range.end), "MMM d, yyyy")}
                </>
              ) : (
                "All Time"
              )}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Time Range</InputLabel>
              <Select
                value={timeRange}
                label="Time Range"
                onChange={handleTimeRangeChange}
              >
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="yearly">Yearly</MenuItem>
                <MenuItem value="all">All Time</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {timeRange === "custom" && (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  selected={startDate}
                  onChange={setStartDate}
                  selectsStart
                  startDate={startDate}
                  endDate={endDate}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="MMMM d, yyyy h:mm aa"
                  customInput={<DateTimeInput label="Start Date & Time" />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  selected={endDate}
                  onChange={setEndDate}
                  selectsEnd
                  startDate={startDate}
                  endDate={endDate}
                  minDate={startDate}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="MMMM d, yyyy h:mm aa"
                  customInput={<DateTimeInput label="End Date & Time" />}
                />
              </Grid>
            </>
          )}
          <Grid item xs={12} sm={6} md={timeRange === "custom" ? 1 : 4}>
            <Box
              display="flex"
              justifyContent="flex-end"
              gap={1}
              className="no-print"
            >
              <Button
                variant="outlined"
                startIcon={<PictureAsPdf />}
                onClick={handleDownloadPDF}
              >
                PDF
              </Button>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={handlePrint}
              >
                Print
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Card>
      {/* Summary Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={4}>
          <Card
            elevation={0}
            sx={{
              height: "100%",
              borderRadius: 2,
              bgcolor: theme.palette.success.main,
              color: "#fff",
              borderLeft: "4px solid",
              borderColor: theme.palette.success.dark,
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
            }}
          >
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
              >
                <Box>
                  <Typography
                    variant="subtitle2"
                    color="#fff"
                    gutterBottom
                    sx={{ opacity: 0.9 }}
                  >
                    TOTAL REVENUE
                  </Typography>
                  {summaryRevenue.map((item, idx) => (
                    <Typography
                      key={item.currency}
                      variant={idx === 0 ? "h4" : "h6"}
                      sx={{
                        fontWeight: 700,
                        color: "#fff",
                        mb: idx === 0 ? 1 : 0,
                      }}
                    >
                      {formatCurrency(item.value, item.currency)}
                      {item.currency !== "AFN" && (
                        <span
                          style={{ fontSize: 14, marginLeft: 4, opacity: 0.8 }}
                        >
                          ({item.currency})
                        </span>
                      )}
                    </Typography>
                  ))}
                </Box>
                <Box
                  display="flex"
                  alignItems="center"
                  color="#fff"
                  sx={{
                    bgcolor: "rgba(255, 255, 255, 0.2)",
                    p: 1,
                    borderRadius: "50%",
                    width: 48,
                    height: 48,
                    justifyContent: "center",
                  }}
                >
                  <TrendingUp fontSize="large" />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card
            elevation={0}
            sx={{
              height: "100%",
              borderRadius: 2,
              bgcolor: theme.palette.error.main,
              color: "#fff",
              borderLeft: "4px solid",
              borderColor: theme.palette.error.dark,
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
            }}
          >
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
              >
                <Box>
                  <Typography
                    variant="subtitle2"
                    color="#fff"
                    gutterBottom
                    sx={{ opacity: 0.9 }}
                  >
                    TOTAL EXPENSES
                  </Typography>
                  {summaryExpenses.map((item, idx) => (
                    <Typography
                      key={item.currency}
                      variant={idx === 0 ? "h4" : "h6"}
                      sx={{
                        fontWeight: 700,
                        color: "#fff",
                        mb: idx === 0 ? 1 : 0,
                      }}
                    >
                      {formatCurrency(item.value, item.currency)}
                      {item.currency !== "AFN" && (
                        <span
                          style={{ fontSize: 14, marginLeft: 4, opacity: 0.8 }}
                        >
                          ({item.currency})
                        </span>
                      )}
                    </Typography>
                  ))}
                </Box>
                <Box
                  display="flex"
                  alignItems="center"
                  color="#fff"
                  sx={{
                    bgcolor: "rgba(255, 255, 255, 0.2)",
                    p: 1,
                    borderRadius: "50%",
                    width: 48,
                    height: 48,
                    justifyContent: "center",
                  }}
                >
                  <MoneyOff fontSize="large" />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card
            elevation={0}
            sx={{
              height: "100%",
              borderRadius: 2,
              bgcolor: theme.palette.info.main,
              color: "#fff",
              borderLeft: "4px solid",
              borderColor: theme.palette.info.dark,
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
            }}
          >
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
              >
                <Box>
                  <Typography
                    variant="subtitle2"
                    color="#fff"
                    gutterBottom
                    sx={{ opacity: 0.9 }}
                  >
                    NET INCOME
                  </Typography>
                  {summaryNetIncome.map((item, idx) => {
                    const isNegative = item.value < 0;
                    const absValue = Math.abs(item.value);
                    return (
                      <Typography
                        key={item.currency}
                        variant={idx === 0 ? "h4" : "h6"}
                        sx={{
                          fontWeight: 700,
                          color: "#fff",
                          display: "flex",
                          alignItems: "center",
                          mb: idx === 0 ? 1 : 0,
                        }}
                      >
                        {isNegative && (
                          <span style={{ marginRight: 4 }}>-</span>
                        )}
                        {formatCurrency(absValue, item.currency)}
                        {item.currency !== "AFN" && (
                          <span
                            style={{
                              fontSize: 14,
                              marginLeft: 4,
                              opacity: 0.8,
                            }}
                          >
                            ({item.currency})
                          </span>
                        )}
                      </Typography>
                    );
                  })}
                </Box>
                <Box
                  display="flex"
                  alignItems="center"
                  color="#fff"
                  sx={{
                    bgcolor: "rgba(255, 255, 255, 0.2)",
                    p: 1,
                    borderRadius: "50%",
                    width: 48,
                    height: 48,
                    justifyContent: "center",
                  }}
                >
                  {summaryNetIncome[0]?.value >= 0 ? (
                    <ArrowDropUp fontSize="large" />
                  ) : (
                    <ArrowDropDown fontSize="large" />
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Revenue" icon={<Receipt />} iconPosition="start" />
          <Tab label="Expenses" icon={<MoneyOff />} iconPosition="start" />
          <Tab label="Trends" icon={<ShowChart />} iconPosition="start" />
          <Tab label="Details" icon={<Summarize />} iconPosition="start" />
        </Tabs>
      </Box>
      {/* Revenue Tab */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 2,
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
              }}
            >
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Revenue Breakdown
                </Typography>
                <Box height={400}>
                  <Bar
                    data={{
                      labels: chartData.incomeBreakdown.labels,
                      datasets: [
                        {
                          data: chartData.incomeBreakdown.data,
                          backgroundColor: chartData.incomeBreakdown.colors,
                          borderRadius: 4,
                        },
                      ],
                    }}
                    options={{
                      onClick: (_, elements) => {
                        if (elements.length > 0) {
                          const index = elements[0].index;
                          handleViewHistory(
                            chartData.incomeBreakdown.history[index],
                            chartData.incomeBreakdown.labels[index]
                          );
                        }
                      },
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          callbacks: {
                            label: function (context) {
                              const label = context.label || "";
                              const value = context.raw || 0;
                              return ` ${label}: ${formatCurrency(value)}`;
                            },
                          },
                        },
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          grid: { drawBorder: false },
                          ticks: {
                            callback: function (value) {
                              return formatCurrency(value);
                            },
                          },
                        },
                        x: {
                          grid: { display: false },
                          ticks: {
                            autoSkip: false,
                            maxRotation: 45,
                            minRotation: 45,
                          },
                        },
                      },
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 2,
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
              }}
            >
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Top Revenue Sources
                </Typography>
                <Box
                  height={400}
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  mt={3}
                >
                  {chartData.topIncomeSources.map((source, index) => (
                    <Box
                      key={index}
                      mb={3}
                      p={2}
                      borderRadius={1}
                      bgcolor={
                        index === 0 ? "primary.lighter" : "background.paper"
                      }
                      borderColor="primary.light"
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={1}
                      >
                        <Typography variant="subtitle1" fontWeight={500}>
                          {source.name.toUpperCase()}
                        </Typography>
                      </Box>
                      <Typography variant="h5" fontWeight={700}>
                        {formatCurrency(source.value)}
                      </Typography>
                      <Button
                        size="small"
                        onClick={() =>
                          handleViewHistory(source.history, source.name)
                        }
                        disabled={
                          !source.history || source.history.length === 0
                        }
                        startIcon={<History />}
                        sx={{ mt: 1 }}
                      >
                        View History
                      </Button>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      {/* Expenses Tab */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 2,
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
              }}
            >
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Expense Breakdown
                </Typography>
                <Box height={400}>
                  <Pie
                    data={{
                      labels: chartData.expenseBreakdown.labels,
                      datasets: [
                        {
                          data: chartData.expenseBreakdown.data,
                          backgroundColor: chartData.expenseBreakdown.colors,
                        },
                      ],
                    }}
                    options={{
                      onClick: (_, elements) => {
                        if (elements.length > 0) {
                          const index = elements[0].index;
                          handleViewHistory(
                            chartData.expenseBreakdown.history[index],
                            chartData.expenseBreakdown.labels[index]
                          );
                        }
                      },
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: "right",
                          labels: {
                            usePointStyle: true,
                            padding: 20,
                          },
                        },
                        tooltip: {
                          callbacks: {
                            label: function (context) {
                              const label = context.label || "";
                              const value = context.raw || 0;
                              const total = context.dataset.data.reduce(
                                (a, b) => a + b,
                                0
                              );
                              const percentage = Math.round(
                                (value / total) * 100
                              );
                              return ` ${label}: ${formatCurrency(
                                value
                              )} (${percentage}%)`;
                            },
                          },
                        },
                      },
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 2,
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
              }}
            >
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Collection Status
                </Typography>
                <Box height={400}>
                  <Doughnut
                    data={{
                      labels: chartData.collectionStatus.labels,
                      datasets: [
                        {
                          data: chartData.collectionStatus.data,
                          backgroundColor: [
                            theme.palette.success.main,
                            theme.palette.warning.main,
                          ],
                          borderWidth: 0,
                          cutout: "70%",
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: "right",
                          labels: {
                            usePointStyle: true,
                            padding: 20,
                          },
                        },
                        tooltip: {
                          callbacks: {
                            label: function (context) {
                              const label = context.label || "";
                              const value = context.raw || 0;
                              const total = context.dataset.data.reduce(
                                (a, b) => a + b,
                                0
                              );
                              const percentage = Math.round(
                                (value / total) * 100
                              );
                              return ` ${label}: ${formatCurrency(
                                value
                              )} (${percentage}%)`;
                            },
                          },
                        },
                      },
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      {/* Trends Tab */}
      {activeTab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 2,
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
              }}
            >
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Financial Trends
                </Typography>
                <Box height={400}>
                  <Line
                    data={{
                      labels: chartData.trends.labels,
                      datasets: [
                        {
                          label: "Revenue",
                          data: chartData.trends.revenue,
                          borderColor: theme.palette.primary.main,
                          backgroundColor: theme.palette.primary.light,
                          tension: 0.3,
                          fill: true,
                        },
                        {
                          label: "Expenses",
                          data: chartData.trends.expenses,
                          borderColor: theme.palette.error.main,
                          backgroundColor: theme.palette.error.light,
                          tension: 0.3,
                          fill: true,
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: "top",
                        },
                        tooltip: {
                          callbacks: {
                            label: function (context) {
                              const label = context.dataset.label || "";
                              const value = context.raw || 0;
                              return ` ${label}: ${formatCurrency(value)}`;
                            },
                          },
                        },
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          grid: { drawBorder: false },
                          ticks: {
                            callback: function (value) {
                              return formatCurrency(value);
                            },
                          },
                        },
                        x: {
                          grid: { display: false },
                        },
                      },
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      {/* Details Tab */}
      {activeTab === 3 && (
        <Grid container spacing={3}>
          {/* Medical Services */}
          <Grid item xs={12}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 2,
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
              }}
            >
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Medical Services
                </Typography>
                <Grid container spacing={3}>
                  {Object.entries(data.medical_services || {}).map(
                    ([key, service]) => (
                      <Grid key={key} item xs={12} sm={6} md={4} lg={3}>
                        <StatCard
                          icon={<MedicalServices />}
                          title={key.replace(/_/g, " ").toUpperCase()}
                          value={service.count || 0}
                          amount={formatCurrency(service.total || 0)}
                          onClick={() =>
                            handleViewHistory(
                              service.history,
                              key.replace(/_/g, " ")
                            )
                          }
                          theme={theme}
                          history={service.history || []}
                          cardSx={{
                            bgcolor: theme.palette.primary.main,
                            color: "#fff",
                          }}
                        />
                      </Grid>
                    )
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          {/* Special Payments */}
          <Grid item xs={12}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 2,
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
              }}
            >
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Special Payments
                </Typography>
                <Grid container spacing={3}>
                  {Object.entries(data.special_payments || {}).map(
                    ([key, payment]) => (
                      <Grid key={key} item xs={12} sm={6} md={4} lg={3}>
                        <StatCard
                          icon={<AccountBalanceWallet />}
                          title={key.replace(/_/g, " ").toUpperCase()}
                          value={payment.count || 0}
                          amount={Object.entries(payment.total || {}).map(
                            ([curr, val], idx) => (
                              <span key={curr}>
                                {formatCurrency(val, curr)}
                                {curr !== "AFN" && (
                                  <span style={{ fontSize: 14, marginLeft: 4 }}>
                                    ({curr})
                                  </span>
                                )}
                                {idx <
                                  Object.entries(payment.total || {}).length -
                                    1 && <br />}
                              </span>
                            )
                          )}
                          onClick={() =>
                            handleViewHistory(
                              payment.history,
                              key.replace(/_/g, " ")
                            )
                          }
                          theme={theme}
                          history={payment.history || []}
                          cardSx={{
                            bgcolor: theme.palette.primary.main,
                            color: "#fff",
                          }}
                        />
                      </Grid>
                    )
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          {/* Expenses */}
          <Grid item xs={12}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 2,
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
              }}
            >
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Expenses
                </Typography>
                <Grid container spacing={3}>
                  {Object.entries(data.expenses || {}).map(([key, expense]) => (
                    <Grid key={key} item xs={12} sm={6} md={4} lg={3}>
                      <StatCard
                        icon={
                          key === "purchase_payments" ? (
                            <PointOfSale />
                          ) : key === "expenses" ? (
                            <MoneyOff />
                          ) : key === "payroll" ? (
                            <AccountTree />
                          ) : key === "advances" ? (
                            <Payments />
                          ) : key === "bonuses" ? (
                            <AccountBalanceWallet />
                          ) : (
                            <MoneyOff />
                          )
                        }
                        title={key.replace(/_/g, " ").toUpperCase()}
                        value={expense.count || 0}
                        amount={formatCurrency(expense.total || 0)}
                        onClick={() =>
                          handleViewHistory(
                            expense.history,
                            key.replace(/_/g, " ")
                          )
                        }
                        theme={theme}
                        history={expense.history || []}
                        highlight="negative"
                        cardSx={{
                          bgcolor: theme.palette.primary.main,
                          color: "#fff",
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      {/* History Dialog */}
      <HistoryDialog
        open={historyDialog.open}
        onClose={() => setHistoryDialog({ ...historyDialog, open: false })}
        history={historyDialog.history}
        title={historyDialog.title}
      />
    </Box>
  );
};
export default FinanceDashboard;
